import {
  Box,
  Button,
  Checkbox,
  Grid,
  MenuItem,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import Custom3dotString from "../../../../../components/Custom3dotString";
import CustomButton from "../../../../../components/CustomButton";
import CustomModal from "../../../../../components/CustomModal/CustomModal";
import CustomTextField from "../../../../../components/CustomTextField";
import CustomSelect from "../../../../../components/Select/Select";
import {
  getPetsCompliantSummaryWoLoader,
  updateProcedureByIdWoLoader,
  updateProcedureDetailByIdWoLoader,
} from "../../../../../redux/reducers/petSlice";
import { AppColors } from "../../../../../util/AppColors";
import { routeList } from "../../../../../util/dropList";
import { billingTabEnum } from "../../../../../util/enum";
import {
  getDoneAtFromNextDueDate,
  getRightPackageUom,
  isBatchAllowedForFacility,
} from "../../../../../util/function";
import { refreshClinicStocksToSession } from "../../../../../util/refreshClinicStocksToSession";

const initValues = {
  batchNumber: null,
  quantity: 1,
  uom: null,
  dosage: "",
  route: null,
  linkToProcedure: "Not linked to a procedure",
  linkProcedureId: null,
  serviceId: null,
  variantId: null,
  billedSeparately: false,
  billingStatus: "Issued",
};
const NOT_LINKED_TO_PROCEDURE = "Not linked to a procedure";

const EditAdministerDrawer = ({ open, onClose, item }) => {
  const dispatch = useDispatch();
  const [values, setValues] = useState(initValues);
  const [isSaving, setIsSaving] = useState(false);

  const reqDet = useSelector((state) => state?.pet?.complaintSummary?.data);
  const appDet = reqDet?.appointment;
  const mandateDataRaw = sessionStorage.getItem("mandateData");
  const { clinicStocks, clinicDet, services, locations, facilities } =
    useMemo(() => {
      if (!mandateDataRaw) {
        return {
          clinicStocks: [],
          clinicDet: {},
          services: [],
          locations: [],
          facilities: [],
        };
      }
      try {
        const reqData = JSON.parse(mandateDataRaw);
        return {
          clinicStocks: Array.isArray(reqData?.stocks) ? reqData.stocks : [],
          clinicDet: reqData?.clinic ?? {},
          services: Array.isArray(reqData?.services) ? reqData.services : [],
          locations: Array.isArray(reqData?.locations) ? reqData.locations : [],
          facilities: Array.isArray(reqData?.facilities)
            ? reqData.facilities
            : [],
        };
      } catch {
        return {
          clinicStocks: [],
          clinicDet: {},
          services: [],
          locations: [],
          facilities: [],
        };
      }
    }, [mandateDataRaw]);

  const getReqFacility = () =>
    facilities?.find((fac) => fac?.facilityId === appDet?.facilityId) ?? null;

  const getBatches = (stkId = null) => {
    const reqStock = clinicStocks?.find((stc) => stc?.stockId === stkId);
    const clinicBatches = reqStock?.stockBatch?.filter(
      (btch) => btch?.createdBy === clinicDet?.id,
    );
    const reqList = clinicBatches
      ?.filter((bat) =>
        isBatchAllowedForFacility(bat, getReqFacility(), locations),
      )
      ?.filter((li) => li?.quantity > 0)
      ?.sort((a, b) => a?.quantity - b?.quantity);
    return reqList?.length > 0
      ? reqList?.map((bat) => {
          const reqLocation = locations?.find(
            (loc) => loc?.id === bat?.locationId,
          );
          return {
            name: bat?.batchNo,
            value: bat?.batchId,
            subunits: bat?.subunits,
            quantity: bat?.quantity,
            locationId: bat?.locationId,
            locationName: reqLocation?.locationName ?? "",
            uom: bat?.qtyMapName ?? "",
            packageUom: bat?.packageUom ?? "",
          };
        })
      : [
          {
            name: "Unknown",
            value: null,
            subunits: null,
            quantity: null,
            locationId: null,
            locationName: "",
            uom: "",
            packageUom: "",
          },
        ];
  };

  const getBatch = (stockId, batchId = null) => {
    if (!stockId || !batchId) return null;
    const reqStock = clinicStocks?.find((stc) => stc?.stockId === stockId);
    return (
      reqStock?.stockBatch
        ?.filter((btch) => btch?.createdBy === clinicDet?.id)
        ?.find((bat) => bat?.batchId === batchId) ?? null
    );
  };

  const buildBatchOption = (stkId, batchId = null, batchNo = null) => {
    const reqBat = batchId ? getBatch(stkId, batchId) : null;
    const reqLocation = locations?.find(
      (loc) => loc?.id === reqBat?.locationId,
    );
    return {
      name: batchNo ?? reqBat?.batchNo ?? "Unknown",
      value: batchId ?? reqBat?.batchId ?? null,
      subunits: reqBat?.subunits ?? null,
      quantity: reqBat?.quantity ?? null,
      locationId: reqBat?.locationId ?? null,
      locationName: reqLocation?.locationName ?? "",
      uom: reqBat?.qtyMapName ?? "",
      packageUom: reqBat?.packageUom ?? "",
    };
  };

  const getBatchesForSelect = (
    stkId = null,
    batchId = null,
    batchNo = null,
  ) => {
    const batchList = getBatches(stkId);
    if (!batchId && !batchNo) return batchList;

    const exists = batchList?.some(
      (bat) =>
        (batchId && String(bat?.value) === String(batchId)) ||
        (batchNo && bat?.name === batchNo),
    );
    if (exists) return batchList;

    const selectedOption = buildBatchOption(stkId, batchId, batchNo);
    if (
      batchList?.length === 1 &&
      batchList?.[0]?.name === "Unknown" &&
      !batchList?.[0]?.value
    ) {
      return [selectedOption];
    }
    return [selectedOption, ...(batchList ?? [])];
  };

  const getUOMListByStockId = (stkId = null) => {
    if (!stkId) return [];
    const stockMat = clinicStocks?.find((stc) => stc?.stockId === stkId);
    return (
      stockMat?.uomMappings
        ?.filter((ml) => ml?.allowSale === "Y" && ml?.show)
        ?.map((ml) => ({
          name: ml?.unit,
          value: ml?.unit,
          conversion: ml?.conversion,
          uomMappingId: ml?.uomMappingId ?? null,
        })) ?? []
    );
  };

  const getProcedureListAsDrop = () => {
    if (!reqDet?.procedures?.length) return [];
    const reqProcedures = reqDet.procedures
      .filter((li) => li?.serviceId && li?.procedureVariantId)
      .map((prcd) => ({
        name: prcd?.procedureName,
        value: prcd?.procedureName,
        procedureId: prcd?.procedureId,
        serviceId: prcd?.serviceId,
        variantId: prcd?.procedureVariantId,
      }));
    reqProcedures.unshift({
      name: "Not linked to a procedure",
      value: "Not linked to a procedure",
      serviceId: null,
      variantId: null,
    });
    return reqProcedures;
  };

  const resolveInitialBatchNumber = (adminItem) => {
    if (!adminItem?.stockId) return null;

    const batchId = adminItem?.batchId ?? null;
    const batchNo = adminItem?.batchNo ?? null;
    const batchList = getBatchesForSelect(adminItem?.stockId, batchId, batchNo);

    if (batchId || batchNo) {
      const match = batchList?.find(
        (bat) =>
          (batchId && String(bat?.value) === String(batchId)) ||
          (batchNo && bat?.name === batchNo),
      );
      if (match) return match;
      return buildBatchOption(adminItem?.stockId, batchId, batchNo);
    }

    return batchList?.[0] ?? null;
  };

  const onChangeValue = (name, value, addObj) => {
    const reqObj = { ...values, [name]: value };
    if (name === "batchNumber") {
      reqObj.batchNumber = value;
      reqObj.batchId = value?.value ?? null;
    }
    if (name === "linkToProcedure") {
      reqObj.linkToProcedure = value;
      reqObj.linkProcedureId = addObj?.procedureId ?? null;
      reqObj.serviceId = addObj?.serviceId ?? null;
      reqObj.variantId = addObj?.variantId ?? null;
    }
    if (name === "route") {
      reqObj.route = value;
    }
    if (name === "dosage") {
      reqObj.dosage = value;
    }
    if (name === "quantity") {
      reqObj.quantity = value;
    }
    if (name === "uom") {
      reqObj.uom = value;
    }
    if (name === "billedSeparately") {
      reqObj.billedSeparately = value;
    }
    if (name === "billingStatus") {
      reqObj.billingStatus = value;
    }
    setValues(reqObj);
  };

  useEffect(() => {
    if (!open) {
      setValues(initValues);
      setIsSaving(false);
      return;
    }
    if (!item) return;

    const isLinked = item?.linked === "Y";
    const isItemRequested =
      item?.status === "requested" ||
      item?.remarks === "Requested" ||
      item?.requestBadgeText;
    const isItemAdministered =
      item?.status === true ||
      item?.status === "true" ||
      item?.remarks === "Completed";
    const batchNumber = resolveInitialBatchNumber(item);
    const reqUomList = getUOMListByStockId(item?.stockId);
    const matUom = reqUomList?.find(
      (ul) => ul?.uomMappingId === item?.uomId || ul?.value === item?.uomName,
    );

    setValues({
      batchNumber,
      batchId: batchNumber?.value ?? item?.batchId ?? null,
      quantity: item?.quantity ?? 1,
      uom: item?.uomName?.length > 0 && matUom ? matUom : null,
      dosage: item?.dosage ?? "",
      route:
        routeList?.find(
          (rl) => rl?.name === item?.route || rl?.value === item?.route,
        ) ?? null,
      linkToProcedure: isLinked
        ? (item?.procedure?.procedureName ?? "Not linked to a procedure")
        : "Not linked to a procedure",
      linkProcedureId: isLinked ? (item?.procedure?.procedureId ?? null) : null,
      serviceId: isLinked
        ? (item?.procedure?.serviceId ?? item?.serviceId ?? null)
        : null,
      variantId: isLinked
        ? (item?.procedure?.procedureVariantId ??
          item?.procedureVariantId ??
          null)
        : null,
      billedSeparately: !!item?.billSeparated,
      billingStatus: isItemRequested
        ? "Requested"
        : isItemAdministered
          ? "Administered"
          : "Issued",
    });
  }, [item, open]);

  const onApiCall = () => {
    dispatch(getPetsCompliantSummaryWoLoader(appDet?.appointmentId));
  };

  const isRequested = item?.status === "requested";

  const isAdministered =
    item?.status === true ||
    item?.status === "true" ||
    item?.remarks === "Completed" ||
    values?.billingStatus === "Administered";

  const isIssued =
    !isRequested &&
    !isAdministered &&
    (item?.status === "issued" ||
      item?.status === true ||
      item?.status === "true" ||
      !!item?.batchId);

  const hideBillSeparately = item?.billing ?? false;

  const dosagePackageUom = getRightPackageUom(
    getBatch(
      item?.stockId,
      values?.batchNumber?.value ?? values?.batchId ?? item?.batchId,
    )?.packageUom,
    true,
  );
  const reqStck = item?.stockId
    ? clinicStocks?.find((stc) => stc?.stockId === item?.stockId)
    : null;
  const dosageField = (
    <CustomTextField
      fullWidth
      handleChange={(e) => onChangeValue("dosage", e?.target?.value)}
      value={values?.dosage}
      suffix={
        dosagePackageUom?.length > 0 ? (
          <Custom3dotString
            str={`${dosagePackageUom}`}
            length={9}
            className="fs12 font-medium"
            capitalize={false}
          />
        ) : (
          ""
        )
      }
    />
  );

  const buildAdministerDetail = () => {
    const reqStock = clinicStocks?.find(
      (stc) => stc?.stockId === item?.stockId,
    );
    const selectedBatchId =
      values?.batchNumber?.value ?? values?.batchId ?? item?.batchId ?? null;
    const reqBatDet = getBatch(item?.stockId, selectedBatchId);
    const uomList = getUOMListByStockId(item?.stockId);
    const matchedUom =
      values?.uom ??
      uomList?.find(
        (ul) => ul?.uomMappingId === item?.uomId || ul?.value === item?.uomName,
      );
    const batUomMatch = uomList?.find(
      (ul) =>
        ul?.uomMappingId === reqBatDet?.mapId ||
        ul?.value === reqBatDet?.uomMapName,
    );
    const linkedProcedureName =
      typeof values?.linkToProcedure === "object"
        ? values?.linkToProcedure?.value
        : values?.linkToProcedure;
    const isLinkedToProcedure =
      linkedProcedureName !== "Not linked to a procedure";
    const linkToProcedure = isLinkedToProcedure; //&& !values?.billedSeparately;
    const isNoAmount = values?.billedSeparately
      ? false
      : reqStock?.autoConsumed || !reqStock?.billable || linkToProcedure;

    const clinicBatches = reqStock?.stockBatch?.filter(
      (btch) => btch?.createdBy === clinicDet?.id,
    );
    const reqBat = reqStock?.autoConsumed
      ? clinicBatches?.[0]
      : selectedBatchId
        ? (clinicBatches?.find((bat) => bat?.batchId === selectedBatchId) ??
          clinicBatches?.[0])
        : clinicBatches?.[0];
    const currLocat = locations?.find((loc) => loc?.id === reqBat?.locationId);
    const stckUnitPrice =
      reqBat?.unitSellingPrice ?? clinicBatches?.[0]?.unitSellingPrice ?? 0;
    const batchConversion = Number(batUomMatch?.conversion ?? 1);
    const selectedConversion = Number(matchedUom?.conversion ?? 1);
    const itmPrice =
      batUomMatch?.uomMappingId === matchedUom?.uomMappingId
        ? Number(stckUnitPrice)
        : (Number(stckUnitPrice) * batchConversion) / selectedConversion;
    const reqUnitPrice = isNoAmount ? 0 : itmPrice;

    const selectedProc =
      (values?.linkProcedureId != null
        ? reqDet?.procedures?.find(
            (pl) => String(pl?.procedureId) === String(values.linkProcedureId),
          )
        : null) ??
      reqDet?.procedures?.find(
        (pl) => pl?.procedureName === linkedProcedureName,
      );
    const serviceId = isLinkedToProcedure
      ? (selectedProc?.serviceId ?? values?.serviceId)
      : (item?.procedure?.serviceId ?? item?.serviceId ?? null);
    const reqServ = services?.find((ser) => ser?.serviceId === serviceId);
    const reqProced = isLinkedToProcedure
      ? selectedProc
      : (item?.procedure ??
        reqDet?.procedures?.find((pl) => pl?.serviceId === serviceId));
    const reqVar = reqServ?.serviceVariants?.find(
      (vnt) =>
        String(vnt?.variantId) ===
        String(
          reqProced?.procedureVariantId ??
            item?.procedureVariantId ??
            values?.variantId,
        ),
    );
    const taxPerent =
      reqServ?.taxApplicability?.toLowerCase() === "yes"
        ? Number(reqBat?.cgst ?? 0) + Number(reqBat?.sgst ?? 0)
        : 0;
    const reqPercent = 100 + Number(taxPerent);
    const quantity = Number(values?.quantity) || 1;
    const reqPrice = Number(reqUnitPrice) * Number(quantity ?? 1);
    const unitPrice = Number(reqPrice) / (reqPercent / 100);
    const prdTaxValue = (taxPerent / 100) * Number(unitPrice);
    const nextDueDate =
      linkToProcedure &&
      reqStock?.category?.toLowerCase() === "vaccines" &&
      reqServ?.nextDueDate?.length > 0
        ? getDoneAtFromNextDueDate(reqServ?.nextDueDate)
        : (item?.nextDueDate ?? null);

    const markAdministered = values?.billingStatus === "Administered";
    const isAlreadyTrue = item?.status === true || item?.status === "true";
    const isRequestedSave =
      !isAlreadyTrue &&
      (item?.status === "requested" ||
        item?.remarks === "Requested" ||
        (!selectedBatchId && !reqBat?.batchId && item?.status === "requested"));

    return {
      detailId: item?.detailId ?? null,
      stockId: item?.stockId ?? null,
      batchId:
        selectedBatchId ?? (reqStock?.autoConsumed ? reqBat?.batchId : null),
      type: "Administered",
      quantity,
      status:
        isAlreadyTrue || markAdministered
          ? true
          : isRequestedSave
            ? "requested"
            : reqStock?.autoConsumed
              ? item?.detailId
                ? item?.status
                : true
              : true,
      remarks: isAlreadyTrue
        ? item?.remarks === "Completed"
          ? "Completed"
          : (item?.remarks ?? "")
        : isRequestedSave
          ? "Requested"
          : markAdministered
            ? "Requested"
            : item?.remarks === "Completed"
              ? "Completed"
              : (item?.remarks ?? ""),
      estimatedCost: isNoAmount ? 0 : (reqPrice ?? 0),
      name: item?.procedureName ?? item?.name,
      procedureName: item?.procedureName ?? item?.name,
      dosage: values?.dosage || null,
      route: values?.route?.value ?? values?.route?.name ?? null,
      isDelete: false,
      serviceId,
      tax: isNoAmount ? 0 : Number(prdTaxValue ?? 0),
      total: isNoAmount ? 0 : Number(reqPrice ?? 0),
      mrpPrice: isNoAmount ? 0 : Number(reqUnitPrice ?? 0),
      procedureVariantId: reqVar?.variantId ?? reqProced?.procedureVariantId,
      patientWorkbook: true,
      mainProcedure: false,
      drugSchedule: reqServ?.drugSchedule ?? null,
      hsnSacCode: reqServ?.hsnSacCode ?? null,
      packageUom: reqBat?.salesUom ?? item?.packageUom ?? null,
      expiryDate: reqBat?.expiryDate ?? item?.expiryDate ?? null,
      batchNo:
        values?.batchNumber?.name || reqBat?.batchNo || item?.batchNo || null,
      billable: reqStock?.billable,
      autoConsumed: reqStock?.autoConsumed,
      linked: linkToProcedure ? "Y" : "N",
      billSeparated: !!values?.billedSeparately,
      billType: reqStock?.autoConsumed
        ? currLocat?.category
          ? billingTabEnum?.[currLocat?.category]
          : "treatment"
        : "treatment",
      uomId: matchedUom?.uomMappingId ?? item?.uomId ?? null,
      uomName: matchedUom?.value ?? item?.uomName ?? null,
      nextDueDate,
      reqProced,
      reqServ,
      reqVar,
      billing: item?.billing ?? false,
      locationId:
        values?.batchNumber?.locationId ??
        reqBat?.locationId ??
        currLocat?.id ??
        null,
      locationName:
        values?.batchNumber?.locationName ?? currLocat?.locationName ?? null,
    };
  };

  const getCurrentLinkName = () =>
    typeof values?.linkToProcedure === "object"
      ? values?.linkToProcedure?.value
      : values?.linkToProcedure;

  const getSelectedProcedureByLink = () => {
    const linkName = getCurrentLinkName();
    if (!linkName || linkName === NOT_LINKED_TO_PROCEDURE) return null;
    if (values?.linkProcedureId != null) {
      return (
        reqDet?.procedures?.find(
          (pl) => String(pl?.procedureId) === String(values.linkProcedureId),
        ) ?? null
      );
    }
    return (
      reqDet?.procedures?.find((pl) => pl?.procedureName === linkName) ?? null
    );
  };

  const buildProcedureUpdateData = (reqProced, reqServ, reqVar, reqObj) => {
    let tax = 0;
    let total = reqVar?.servicePrice ?? reqProced?.serviceCharge ?? 0;
    if (reqServ?.taxApplicability?.toLowerCase() === "yes") {
      tax +=
        (Number(reqVar?.servicePrice ?? 0) / 100) * Number(reqServ?.tax ?? 0);
      total += tax;
    }

    return {
      serviceCharge: reqProced?.serviceCharge,
      serviceType: reqProced?.serviceType,
      appointmentId: reqDet?.appointment?.appointmentId,
      procedureDetails: [reqObj],
      tax: Number(Number(tax) * Number(reqProced?.quantity ?? 1)),
      total: Number(Number(total) * Number(reqProced?.quantity ?? 1)),
      mrpPrice: Number(reqVar?.servicePrice ?? 0),
      procedureVariantId: reqVar?.variantId,
      quantity: Number(reqProced?.quantity ?? 1),
      hsnSacCode: reqServ?.hsnSacCode ?? null,
      paymentId: reqDet?.paymentId ?? null,
      doseInSeries: reqProced?.doseInSeries ?? null,
      serviceRemainderId: reqProced?.serviceRemainderId ?? null,
    };
  };

  const handleSave = () => {
    if (!item) return;

    const detailPayload = buildAdministerDetail();
    const { reqProced, reqServ, reqVar, ...reqObj } = detailPayload;
    const currentLinkName = getCurrentLinkName();
    const isNotLinkedSelection = currentLinkName === NOT_LINKED_TO_PROCEDURE;
    const selectedTargetProc = getSelectedProcedureByLink();
    const sourceProcedureId = item?.procedure?.procedureId ?? null;
    const targetProcedureId = selectedTargetProc?.procedureId ?? null;
    const shouldMoveDetail =
      !!selectedTargetProc &&
      String(targetProcedureId ?? "") !== String(sourceProcedureId ?? "");

    setIsSaving(true);
    const finalize = (success) => {
      setIsSaving(false);
      if (success) {
        refreshClinicStocksToSession(dispatch);
        onApiCall();
        onClose();
      }
    };

    const runOldUpdate = (procedureId, nextReqObj = reqObj) => {
      if (!procedureId || !reqProced?.procedureId || !reqServ) {
        finalize(false);
        return;
      }
      dispatch(
        updateProcedureByIdWoLoader({
          id: procedureId,
          data: buildProcedureUpdateData(
            reqProced,
            reqServ,
            reqVar,
            nextReqObj,
          ),
        }),
      ).then((res) => finalize(!!res?.payload));
    };

    if (!shouldMoveDetail) {
      const updateProcedureId = isNotLinkedSelection
        ? sourceProcedureId
        : (targetProcedureId ?? sourceProcedureId);
      runOldUpdate(updateProcedureId);
      return;
    }

    dispatch(
      updateProcedureDetailByIdWoLoader({
        procedureId: targetProcedureId,
        procedureDetailId: reqObj?.detailId,
      }),
    ).then((res) => {
      if (!res?.payload) {
        finalize(false);
        return;
      }

      const payload = res?.payload?.data ?? res?.payload ?? {};
      const nextProcedureId =
        payload?.procedureId ?? payload?.id ?? targetProcedureId;
      const nextDetailId = payload?.detailId ?? reqObj?.detailId;

      runOldUpdate(nextProcedureId, {
        ...reqObj,
        detailId: nextDetailId,
      });
    });
  };

  return (
    <CustomModal
      open={open}
      onClose={onClose}
      header="Edit item"
      modal
      modalWidth={40}
      reduceHeaderSize
    >
      <Box
        display="flex"
        flexDirection="column"
        sx={{ height: "calc(100vh - 75px)" }}
      >
        <Box p={3} flexGrow={1} overflow="auto">
          <Box mb={3}>
            <Typography
              sx={{
                fontSize: "11px",
                fontWeight: 700,
                color: "#616161",
                letterSpacing: "0.5px",
                mb: 1,
                textTransform: "uppercase",
              }}
            >
              ITEM
            </Typography>
            <TextField
              fullWidth
              size="small"
              disabled
              value={item?.procedureName ?? item?.name ?? ""}
              sx={{
                backgroundColor: "#f5f5f5",
                "& .MuiOutlinedInput-root": { borderRadius: "6px" },
              }}
            />
          </Box>

          <Grid container spacing={2} mb={3}>
            <Grid item xs={6}>
              <Typography
                sx={{
                  fontSize: "11px",
                  fontWeight: 700,
                  color: "#616161",
                  letterSpacing: "0.5px",
                  mb: 1,
                  textTransform: "uppercase",
                }}
              >
                BATCH / LOT NO.
              </Typography>
              <CustomSelect
                list={getBatchesForSelect(
                  item?.stockId,
                  values?.batchNumber?.value ?? item?.batchId,
                  values?.batchNumber?.name ?? item?.batchNo,
                )}
                value={values?.batchNumber}
                handleChange={(e, value) => onChangeValue("batchNumber", value)}
                name="batchNumber"
                search
                isBatchNumber
                disabled={
                  hideBillSeparately ||
                  !item?.stockId ||
                  clinicStocks?.find((stc) => stc?.stockId === item?.stockId)
                    ?.autoConsumed
                }
              />
            </Grid>
            <Grid item xs={3}>
              <Typography
                sx={{
                  fontSize: "11px",
                  fontWeight: 700,
                  color: "#616161",
                  letterSpacing: "0.5px",
                  mb: 1,
                  textTransform: "uppercase",
                }}
              >
                BILLABLE QTY
              </Typography>
              <TextField
                type="number"
                fullWidth
                size="small"
                value={values?.quantity}
                onChange={(e) => onChangeValue("quantity", e.target.value)}
                inputProps={{ min: 1 }}
                sx={{
                  backgroundColor: "#fff",
                  "& .MuiOutlinedInput-root": { borderRadius: "6px" },
                }}
                // disabled={hideBillSeparately}
              />
            </Grid>
            <Grid item xs={3}>
              <Typography
                sx={{
                  fontSize: "11px",
                  fontWeight: 700,
                  color: "#616161",
                  letterSpacing: "0.5px",
                  mb: 1,
                  textTransform: "uppercase",
                }}
              >
                UOM
              </Typography>
              <CustomSelect
                list={getUOMListByStockId(item?.stockId)}
                value={values?.uom}
                search
                handleChange={(e, value) => onChangeValue("uom", value)}
              />
            </Grid>
          </Grid>

          <Grid container spacing={2} mb={3}>
            <Grid item xs={6}>
              <Typography
                sx={{
                  fontSize: "11px",
                  fontWeight: 700,
                  color: "#616161",
                  letterSpacing: "0.5px",
                  mb: 1,
                  textTransform: "uppercase",
                }}
              >
                DOSE
              </Typography>
              {item?.stockId ? (
                <Tooltip
                  title={
                    <>
                      <Typography className="fs12" display="block">
                        Approximated Dosage: {reqStck?.dosage ?? "-"}
                      </Typography>
                      <Typography className="fs12" display="block">
                        Dosage Type: {reqStck?.dosageType ?? "-"}
                      </Typography>
                    </>
                  }
                >
                  <span style={{ display: "inline-block", width: "100%" }}>
                    {dosageField}
                  </span>
                </Tooltip>
              ) : (
                dosageField
              )}
            </Grid>
            <Grid item xs={6}>
              <Typography
                sx={{
                  fontSize: "11px",
                  fontWeight: 700,
                  color: "#616161",
                  letterSpacing: "0.5px",
                  mb: 1,
                  textTransform: "uppercase",
                }}
              >
                ROUTE
              </Typography>
              <CustomSelect
                list={routeList}
                value={values?.route}
                search
                handleChange={(e, value) => onChangeValue("route", value)}
              />
            </Grid>
          </Grid>

          <Box mb={3}>
            <Typography
              sx={{
                fontSize: "11px",
                fontWeight: 700,
                color: "#616161",
                letterSpacing: "0.5px",
                mb: 1,
                textTransform: "uppercase",
              }}
            >
              LINK TO PROCEDURE
            </Typography>
            <TextField
              select
              fullWidth
              size="small"
              value={
                typeof values?.linkToProcedure === "object"
                  ? values?.linkToProcedure?.value
                  : values?.linkToProcedure
              }
              onChange={(e) => {
                const selected = getProcedureListAsDrop().find(
                  (opt) => opt.value === e.target.value,
                );
                onChangeValue("linkToProcedure", e.target.value, {
                  procedureId: selected?.procedureId ?? null,
                  serviceId: selected?.serviceId ?? null,
                  variantId: selected?.variantId ?? null,
                });
              }}
              sx={{
                backgroundColor: "#fff",
                "& .MuiOutlinedInput-root": { borderRadius: "6px" },
              }}
            >
              {getProcedureListAsDrop().map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>
                  {opt.name}
                </MenuItem>
              ))}
            </TextField>
            <Typography sx={{ fontSize: "12px", color: "#757575", mt: 0.8 }}>
              This is a clinical record of which procedure the item was used
              for.
            </Typography>
          </Box>

          {!hideBillSeparately ? (
            <Box mb={2}>
              <div className="flex-row">
                <Checkbox
                  checked={!!values?.billedSeparately}
                  onChange={(e) =>
                    onChangeValue("billedSeparately", e.target.checked)
                  }
                  sx={{
                    color: AppColors.appPrimary,
                    "&.Mui-checked": { color: AppColors.appPrimary },
                  }}
                />
                <div className="ml10">
                  <Typography
                    sx={{
                      fontSize: "14px",
                      fontWeight: 600,
                      color: "#212121",
                      mt: -0.3,
                    }}
                  >
                    Bill separately (extra line item)
                  </Typography>
                  <Typography sx={{ fontSize: "12px", color: "#757575" }}>
                    Suggested automatically — override if this case needs
                    something different.
                  </Typography>
                </div>
              </div>
            </Box>
          ) : null}

          <Box mb={2}>
            <Typography
              sx={{
                fontSize: "11px",
                fontWeight: 700,
                color: "#616161",
                letterSpacing: "0.5px",
                mb: 1,
                textTransform: "uppercase",
              }}
            >
              BILLING STATUS
            </Typography>
            <Box
              display="flex"
              gap={1}
              mb={1}
              alignItems="center"
              flexWrap="wrap"
            >
              {isRequested ? (
                <Box
                  sx={{
                    backgroundColor: "#fff3e0",
                    color: "#e65100",
                    fontSize: "12px",
                    fontWeight: 600,
                    px: 1.2,
                    py: 0.3,
                    borderRadius: "4px",
                    border: "1px solid #ffe082",
                  }}
                >
                  Requested
                </Box>
              ) : isAdministered ? (
                <Box
                  sx={{
                    backgroundColor: "#e8f5e9",
                    color: "#2e7d32",
                    fontSize: "12px",
                    fontWeight: 600,
                    px: 1.2,
                    py: 0.3,
                    borderRadius: "4px",
                    border: "1px solid #c8e6c9",
                  }}
                >
                  Administered
                </Box>
              ) : isIssued ? (
                <>
                  <Box
                    sx={{
                      backgroundColor: "#e0f2fe",
                      color: "#0284c7",
                      fontSize: "12px",
                      fontWeight: 600,
                      px: 1.2,
                      py: 0.3,
                      borderRadius: "4px",
                      border: "1px solid #bae6fd",
                    }}
                  >
                    Issued
                  </Box>
                  <Button
                    size="small"
                    variant="contained"
                    onClick={() =>
                      onChangeValue("billingStatus", "Administered")
                    }
                    sx={{
                      backgroundColor: "#003e7e",
                      color: "#fff",
                      textTransform: "none",
                      fontWeight: 600,
                      fontSize: "12px",
                      height: "30px",
                      px: 1.5,
                      boxShadow: "none",
                      whiteSpace: "nowrap",
                      "&:hover": {
                        backgroundColor: "#002b58",
                        boxShadow: "none",
                      },
                    }}
                  >
                    Mark Administered
                  </Button>
                </>
              ) : null}
            </Box>
          </Box>

          {hideBillSeparately ? (
            <Typography
              sx={{ fontSize: "12px", color: "#757575", mb: 2, mt: -1 }}
            >
              Requested from pharmacy — billed on a separate invoice, not part
              of the clinic total.
            </Typography>
          ) : null}
        </Box>

        <Box
          display="flex"
          justifyContent="flex-end"
          alignItems="center"
          gap={1.5}
          px={3}
          py={2}
          borderTop="1px solid #e0e0e0"
          backgroundColor="#fafafa"
        >
          <Button
            onClick={onClose}
            sx={{
              color: "#616161",
              textTransform: "none",
              fontWeight: 600,
              px: 2,
            }}
          >
            Cancel
          </Button>
          <CustomButton
            text="Save changes"
            smallBtn
            disabled={isSaving}
            onClick={handleSave}
          />
        </Box>
      </Box>
    </CustomModal>
  );
};

export default EditAdministerDrawer;
